import { useEffect, useRef, type JSX, type ReactNode } from 'react';
import gsap from 'gsap';
import { prefersReducedMotion } from '../preferences/reducedMotion.js';
import './GameEndClose.css';

interface GameEndCloseProps {
  /** OPTIONAL — content rendered on the leaf's front face. This component is
   *  intentionally content-AGNOSTIC: it has no idea what's on the prior page,
   *  the CALLER (the mock here, the production phase router in real wiring)
   *  passes whatever recto was on the spread before the close fired. When omitted,
   *  the leaf is paper with twin rules only — a generic "a page closing." */
  rectoContent?: ReactNode;
  /** Fires the instant the leaf has SETTLED at -180° (rotation complete) but is
   *  still opaque on top of the spread. The caller uses this to swap the
   *  underlying verso from the prior phase's content to the GameEnd reveal —
   *  the swap happens UNDER the opaque leaf back, so the user never sees the
   *  prior content in a clean post-close state, and the GameEnd reveal then
   *  emerges through the leaf's own fade-out. Without this hook the prior
   *  verso lingers briefly after the leaf fades and reads as a pop. */
  onLeafSettled?: () => void;
  /** Fires once the leaf has folded AND faded — the close is fully over. */
  onComplete: () => void;
}

/**
 * The villager close cinematic — a SINGLE folding leaf representing "the recto
 * of the open spread the prior phase was on, folding shut onto THIS book."
 *
 * Crucially this is NOT a second Spread element: the resting closed `<Spread>`
 * in GameEndVillage is the book throughout. The leaf is rendered as that
 * Spread's overlay child, positioned IMMEDIATELY TO THE RIGHT of the closed
 * page (`left: 100%`, same width and height) — so at rotateY=0 the eye reads
 * "closed page on the left + recto leaf on the right = an open spread, this
 * very book one moment before closing." The leaf then rotates 0° → -180° around
 * its left edge (the closed page's right edge — the spine of the prior open
 * book) and lands face-down on top of the closed page, then fades to reveal
 * the resting closed book underneath. ONE book; the leaf is just a gesture.
 *
 * The leaf is intentionally CONTENT-AGNOSTIC (paper + recto twin rules, no
 * inner content). GameEnd is reached from many phases (Discussion, RoleReveal,
 * GhostGuessing, GuessReveal, ClueGiving) whose rectos carried different
 * content — the transition can't know what was on the page it's closing, and
 * putting GameEnd's own content on the leaf would pre-show the reveal that
 * lands AFTER the close (the closed book reveals "Villagers win." once the
 * leaf has folded; GameEndVillage gates the verso content on `transitioning`).
 *
 * Reduced motion: skip; onComplete fires next tick so only the resting closed
 * book renders, no leaf gesture.
 */
export function GameEndClose({ rectoContent, onLeafSettled, onComplete }: GameEndCloseProps): JSX.Element {
  const leafRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reduced = prefersReducedMotion();
    if (reduced || !leafRef.current) {
      /* Bail path: settle and complete fire back-to-back so the caller's
         verso-swap + leaf-unmount both run in the same tick — there's no leaf
         to hide a swap under, but there's also no flash to mask. */
      onLeafSettled?.();
      onComplete();
      return;
    }
    const tl = gsap.timeline({ onComplete });
    /* Slow-lift / fast-apex / slow-settle: power3.inOut over 1.1s. Mirrors
       PageTurnLeaf's feel so this close reads as the same physical chapbook
       that the Lobby→Meeting page-turn established. */
    tl.fromTo(
      leafRef.current,
      { rotateY: 0, opacity: 1, filter: 'drop-shadow(0 0 0 oklch(22% 0.024 60 / 0))' },
      { rotateY: -180, duration: 1.1, ease: 'power3.inOut' },
      0,
    );
    /* Warm Aged-Ink drop-shadow breathing through the lift — rises in the
       first half (the leaf is off the desk), settles in the second (laying
       back down on the closed book). Same shape as PageTurnLeaf for parity. */
    tl.to(leafRef.current, { filter: 'drop-shadow(0 14px 28px oklch(22% 0.024 60 / 0.22))', duration: 0.55, ease: 'power2.out' }, 0);
    tl.to(leafRef.current, { filter: 'drop-shadow(0 0 0 oklch(22% 0.024 60 / 0))', duration: 0.55, ease: 'power2.in' }, 0.55);
    /* Hide the front content right at the apex so children with their own
       compositing contexts don't render through `backface-visibility: hidden`
       in Chromium. */
    if (contentRef.current) {
      tl.to(contentRef.current, { opacity: 0, duration: 0.12, ease: 'power1.in' }, 0.5);
    }
    /* Fire onLeafSettled the instant rotation reaches -180° (leaf back fully
       covers the spread, opaque). The caller swaps the underlying verso here
       — the prior phase's content is hidden by the leaf back, so the swap is
       invisible; the GameEnd reveal then emerges through the leaf's fade-out
       below rather than popping in after the leaf is already gone. */
    tl.call(() => { onLeafSettled?.(); }, [], 1.1);
    /* Fade the leaf after it lands, revealing the (now-swapped) GameEnd
       content underneath. */
    tl.to(leafRef.current, { opacity: 0, duration: 0.2, ease: 'power2.in' }, 1.05);
    return () => { tl.kill(); };
  }, [onComplete]);

  return (
    <div className="susdle-gameend-close-leaf" ref={leafRef} aria-hidden>
      <div className="susdle-gameend-close-leaf__face susdle-gameend-close-leaf__face--front">
        {rectoContent !== undefined ? (
          <div className="susdle-gameend-close-leaf__content" ref={contentRef}>
            {rectoContent}
          </div>
        ) : null}
      </div>
      <div className="susdle-gameend-close-leaf__face susdle-gameend-close-leaf__face--back" />
    </div>
  );
}
